"use client";

// Last-resort error boundary. Next.js renders this instead of the root layout
// when a render error escapes every other boundary (including errors thrown in
// `layout.tsx` itself), so it has to ship its own <html>/<body> and cannot use
// LocaleProvider / ThemeProvider — they live inside the layout that just
// failed. Hence the inlined locale strings and theme bootstrap below.
//
// Sentry.captureException is what the @sentry/nextjs setup warning asks for:
// React rendering errors are otherwise never reported.
import * as Sentry from "@sentry/nextjs";
import { useEffect } from "react";
import "./globals.css";
import { defaultLocale, isLocale, localeCookieName } from "@/i18n/config";
import { defaultTheme, themeCookieName } from "@/lib/theme";

const messages = {
  en: {
    title: "Something went wrong",
    description:
      "An unexpected error occurred. Try again, and contact an administrator if it keeps happening.",
    retry: "Try again",
  },
  ru: {
    title: "Что-то пошло не так",
    description:
      "Произошла непредвиденная ошибка. Попробуйте ещё раз, а если она повторяется — обратитесь к администратору.",
    retry: "Повторить",
  },
  zh: {
    title: "出现错误",
    description: "发生了意外错误。请重试；如果问题持续出现，请联系管理员。",
    retry: "重试",
  },
} as const;

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

// Same pre-paint theme bootstrap as the root layout, duplicated because that
// layout is not rendered on this path.
const themeBootstrap = `(function(){try{var c=document.cookie.match(/(?:^|; )${themeCookieName}=([^;]+)/);var t=c?decodeURIComponent(c[1]):'${defaultTheme}';var dark=t==='dark'||(t==='system'&&window.matchMedia('(prefers-color-scheme: dark)').matches);if(dark)document.documentElement.classList.add('dark');}catch(e){}})();`;

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  const cookieLocale = readCookie(localeCookieName);
  const locale = isLocale(cookieLocale) ? cookieLocale : defaultLocale;
  const t = messages[locale] ?? messages[defaultLocale];

  return (
    <html lang={locale} className="h-full antialiased" suppressHydrationWarning>
      <head>
        <script dangerouslySetInnerHTML={{ __html: themeBootstrap }} />
      </head>
      <body className="min-h-full flex flex-col bg-background text-foreground">
        <main className="flex flex-1 items-center justify-center p-6">
          <div className="w-full max-w-md rounded-xl border border-border bg-surface p-6 shadow-sm">
            <h1 className="text-lg font-semibold text-danger">{t.title}</h1>
            <p className="mt-2 text-sm text-muted">{t.description}</p>
            {error.digest && (
              <p className="mt-3 font-mono text-xs text-muted">{error.digest}</p>
            )}
            <button
              type="button"
              onClick={reset}
              className="mt-5 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-contrast hover:bg-accent-hover"
            >
              {t.retry}
            </button>
          </div>
        </main>
      </body>
    </html>
  );
}
